import { type ComponentProps, useState } from "react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import {
  BuyingSignalCard,
  BuyingSignalsSkeleton,
  ScrollableLeadList,
} from "components/document_details/BuyingSignalCard";
import DocumentDrawer from "components/document_details/DocumentDrawer";
import PageBadge from "components/document_details/PageBadge";
import { convertDocumentFiltersToSearchRequest, DocumentFilters } from "components/documents/utils";
import Button from "components/ui/button/Button";
import EmptyState from "components/ui/empty_state/EmptyState";
import { SourceLocation } from "lib/core_components/fileViewerAnnotations";
import {
  DocumentBuyingSignalLead,
  useDocumentBuyingSignals,
} from "lib/hooks/api/documents/useDocumentBuyingSignals";
import { useDocumentBuyingSignalsStart } from "lib/hooks/api/documents/useDocumentBuyingSignalsStart";
import { useDocumentBuyingSignalsUpdates } from "lib/hooks/api/documents/useDocumentBuyingSignalsUpdates";
import { CurrentUserData } from "../../lib/hooks/api/users/useCurrentUser";

type DocumentBuyingSignalsResultsProps = {
  filters: DocumentFilters;
  currentUser: CurrentUserData | undefined;
  enabled?: boolean;
};

type OpenDocument = {
  documentId: string;
  locations: SourceLocation[];
};

function humaniseLeadType(leadType: string): string {
  return leadType.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

/**
 * "Buying signals" tab of the documents search page. Runs an async pipeline over the same filtered
 * document population as the Documents tab and surfaces the strongest buying signals (leads) across
 * it, ranked strongest-first, each linking back into its source document. Cache-first: a completed
 * run for the current filters renders instantly; otherwise the user runs it explicitly.
 */
export default function DocumentBuyingSignalsResults({
  filters,
  currentUser,
  enabled,
}: DocumentBuyingSignalsResultsProps) {
  const [openDocument, setOpenDocument] = useState<OpenDocument | null>(null);

  // Buying signals is keyword-driven server-side; the free-text `title` from the search box is
  // intentionally not sent (so it's also excluded from the operation's cache key).
  const request = {
    ...convertDocumentFiltersToSearchRequest(filters, currentUser, {
      current: 1,
      pageSize: 10,
    }),
    title: undefined,
  };

  const {
    data: cached,
    isInitialLoading: isCachedLoading,
    isError: isCachedError,
  } = useDocumentBuyingSignals(request, { enabled });

  // The start hook writes the running status straight into the cached query on success, bridging
  // the gap until the first PubSub `running` event arrives.
  const { mutate: startSignals, isLoading: isStarting } = useDocumentBuyingSignalsStart();

  const operationId = cached?.operationId ?? null;
  useDocumentBuyingSignalsUpdates({
    request,
    operationId,
    enabled: cached?.status === "running",
  });

  const handleRun = () => startSignals({ request });

  if (!enabled) {
    return (
      <SignalsMessage
        ariaLabel="Buying signals need filters"
        type="startingState"
        heading="Apply filters to surface buying signals"
        description="Add keywords or other filters and we'll find the strongest buying signals across the matching documents."
      />
    );
  }

  if (isCachedLoading) {
    return <BuyingSignalsSkeleton />;
  }

  if (isCachedError) {
    return (
      <SignalsMessage
        ariaLabel="Buying signals load error"
        type="loadError"
        heading="Couldn't load buying signals"
        description="We couldn't load buying signals right now. Please try again later."
      />
    );
  }

  const status = cached?.status ?? "missing";

  if (status === "running" || isStarting) {
    return (
      <Container column gap={12} aria-label="Generating buying signals">
        <Text description color="sysTextSecondary">
          Scanning the matching documents for buying signals…
        </Text>
        <BuyingSignalsSkeleton />
      </Container>
    );
  }

  if (status === "failed") {
    return (
      <SignalsMessage
        ariaLabel="Buying signals failed"
        type="loadError"
        heading="Couldn't generate buying signals"
        description="Something went wrong generating buying signals. Please try again."
        actionLabel="Run again"
        onAction={handleRun}
      />
    );
  }

  if (status === "missing") {
    return (
      <SignalsMessage
        ariaLabel="Buying signals not run"
        type="startingState"
        heading="Find buying signals across these documents"
        description="Run an AI scan over the documents matching your current filters to surface the strongest buying signals."
        actionLabel="Run buying signals"
        onAction={handleRun}
        actionLoading={isStarting}
      />
    );
  }

  const result = cached?.result;
  const leads = result?.leads ?? [];

  if (leads.length === 0) {
    return (
      <SignalsMessage
        ariaLabel="No buying signals"
        type="noResults"
        heading="No strong buying signals for these filters"
        description="Nothing in the matching documents stood out against your team profile. Try broadening your filters or adding keywords to your feed settings."
        actionLabel="Run again"
        onAction={handleRun}
      />
    );
  }

  return (
    <Container column gap={16}>
      <Flex column gap={4}>
        <Text h3 bold>
          {leads.length} buying signals across {result?.documentCount ?? 0} matching documents
        </Text>
        <Text description color="sysTextSecondary">
          Ranked by relevance to your team profile
        </Text>
      </Flex>

      <ScrollableLeadList>
        <Flex column gap={12}>
          {leads.map((lead, index) => (
            <LeadRow
              key={`${lead.documentId}-${lead.chunkNumber ?? index}-${lead.leadTitle}`}
              lead={lead}
              onOpenPage={() =>
                setOpenDocument({
                  documentId: lead.documentId,
                  locations: [{ pageNumber: lead.pageNumbers[0] ?? 1, bbox: null }],
                })
              }
            />
          ))}
        </Flex>
      </ScrollableLeadList>

      {openDocument && (
        <DocumentDrawer
          open={!!openDocument}
          onOpenChange={(open) => {
            if (!open) setOpenDocument(null);
          }}
          documentId={openDocument.documentId}
          filters={filters}
          initialSourceLocations={openDocument.locations}
        />
      )}
    </Container>
  );
}

type SignalsMessageProps = {
  ariaLabel: string;
  type: ComponentProps<typeof EmptyState>["type"];
  heading: string;
  description: string;
  actionLabel?: string;
  onAction?: () => void;
  actionLoading?: boolean;
};

// Single source of chrome for the missing / failed / empty / load-error panels: an EmptyState plus
// an optional run action, wrapped so each panel exposes a stable aria-label for querying.
function SignalsMessage({
  ariaLabel,
  type,
  heading,
  description,
  actionLabel,
  onAction,
  actionLoading,
}: SignalsMessageProps) {
  return (
    <Container column gap={16} aria-label={ariaLabel}>
      <EmptyState width="100%" type={type} heading={heading} description={description} />
      {actionLabel && onAction && (
        <Flex>
          <Button onClick={onAction} loading={actionLoading}>
            {actionLabel}
          </Button>
        </Flex>
      )}
    </Container>
  );
}

type LeadRowProps = {
  lead: DocumentBuyingSignalLead;
  onOpenPage: () => void;
};

function LeadRow({ lead, onOpenPage }: LeadRowProps) {
  const pageNumber = lead.pageNumbers[0];
  const documentLabel = lead.documentName ?? "Untitled document";
  const sourceLabel = lead.buyerName ? `${documentLabel} · ${lead.buyerName}` : documentLabel;

  return (
    <BuyingSignalCard column gap={12}>
      <Flex column gap={4}>
        <Text bold>{lead.leadTitle}</Text>
        <Text description color="sysTextSecondary">
          {humaniseLeadType(lead.leadType)} · {sourceLabel}
        </Text>
      </Flex>
      {lead.leadMotivation && <Text>{lead.leadMotivation}</Text>}
      {pageNumber !== undefined && (
        <Flex>
          <PageBadge type="button" onClick={onOpenPage}>
            View in document [p{pageNumber}]
          </PageBadge>
        </Flex>
      )}
    </BuyingSignalCard>
  );
}

const Container = styled(Flex)({
  padding: "16px 0",
  width: "100%",
});
